import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _dec, _dec2, _class, _ComponentPageStateConfig;
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { PageStateLogger } from "abreact/core/pagestate/PageStateLogger";
import { makeObservable } from "abweb/utils/ConditionalMobx";
import { LocallyUniqueIdSet } from "abweb/utils/LocallyUniqueIdSet";
import { action, ObservableMap } from "mobx";

/**
 * Stored in association with every component (in a weak map), an instance of this object is used to provide
 * access to the page state objects necessary to operate page state (such as it's component page state and the
 * page instance state storage it is stored within) and also top-level functions to manipulate page state.
 */

export let ComponentPageStateConfig = (_dec = action.bound, _dec2 = action.bound, _class = (_ComponentPageStateConfig = class ComponentPageStateConfig {
  constructor(componentName, useMobx) {
    _defineProperty(this, "_localObservableDefaultStorage", void 0);
    _defineProperty(this, "_localNonObservableDefaultStorage", void 0);
    _defineProperty(this, "_stateStorage", void 0);
    _defineProperty(this, "_useMobx", true);
    _defineProperty(this, "_pageStateStorage", void 0);
    _defineProperty(this, "_isCollectingDefaults", true);
    _defineProperty(this, "_componentName", void 0);
    _defineProperty(this, "uniqueId", ComponentPageStateConfig.idSet.generateId());
    this._localObservableDefaultStorage = new ObservableMap();
    this._localNonObservableDefaultStorage = new Map();
    this._componentName = componentName;
    if (useMobx !== undefined) {
      this._useMobx = useMobx;
    }
    PageStateLogger.maybeLog("state", `ComponentPageStateConfig constructed for ${componentName} (config:${this.uniqueId})`);
    makeObservable(this);
  }
  get componentName() {
    return this._componentName;
  }
  get isCollectingDefaults() {
    return this._isCollectingDefaults;
  }
  set isCollectingDefaults(value) {
    if (value !== this._isCollectingDefaults) {
      this._isCollectingDefaults = value;
      PageStateLogger.maybeLog("state", `${this._componentName} isCollectingDefaults=${value} (config:${this.uniqueId})`);
    }
  }
  setComponentPageState(pageState) {
    this._stateStorage = pageState;
  }
  setPageInstanceStateStorage(pageStateStorage) {
    this._pageStateStorage = pageStateStorage;
  }
  get pageInstanceStateStorage() {
    return this._pageStateStorage;
  }
  get useMobx() {
    return this._useMobx;
  }
  set useMobx(value) {
    this._useMobx = value;
  }
  get componentPageState() {
    return this._stateStorage;
  }
  get qualifiedId() {
    return this._stateStorage?.qualifiedId;
  }
  reportAction(methodName, key, value) {
    const stringValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : value && value.toString();
    PageStateLogger.maybeLog("state", `${methodName} for ${key}: ${stringValue} on ${this.qualifiedId ?? "unknown"} (config:${this.uniqueId})`);
    return value;
  }
  setDefaultValue(key, value) {
    if (this.useMobx) {
      this._localObservableDefaultStorage.set(key, value);
    } else {
      this._localNonObservableDefaultStorage.set(key, value);
    }
    this.reportAction(`set ${this.useMobx ? "observable" : "non-observable"} default`, key, value);
  }
  setSilentDefaultValue(key, value) {
    this._localNonObservableDefaultStorage.set(key, value);
    this.reportAction(`set silent default`, key, value);
  }
  setSilentValue(key, value) {
    if (!this._stateStorage) {
      this.setSilentDefaultValue(key, value);
    } else {
      this._stateStorage.setSilent(key, value);
    }
  }
  setValue(key, value, stateUpdateCallBack) {
    if (this.isCollectingDefaults || this._stateStorage == null) {
      this.setDefaultValue(key, value);
    } else {
      this._localObservableDefaultStorage.delete(key);
      this._localNonObservableDefaultStorage.delete(key);
      this._stateStorage.set(key, value);
      if (stateUpdateCallBack) {
        this.reportAction("calling update callback", key, value);
        stateUpdateCallBack();
      }
    }
  }
  getValue(key) {
    let value = undefined;
    if (this._stateStorage) {
      value = this._stateStorage.get(key);
    }
    if (value === undefined) {
      value = this._localObservableDefaultStorage.has(key) ? this._localObservableDefaultStorage.get(key) : this._localNonObservableDefaultStorage.get(key);
      this.reportAction("get default value", key, value);
    }
    return value;
  }
}, _defineProperty(_ComponentPageStateConfig, "idSet", new LocallyUniqueIdSet()), _ComponentPageStateConfig), _applyDecoratedDescriptor(_class.prototype, "setDefaultValue", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "setDefaultValue"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setValue", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "setValue"), _class.prototype), _class);
//# sourceMappingURL=IComponentPageStateConfig.js.map
